import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, CalendarPlus, ChevronDown } from "lucide-react";
import { api, setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { Ticker } from "@/components/Ticker";
import { SectionVideo } from "@/components/SectionVideo";
import { JoinForm } from "@/components/JoinForm";

const EventRsvp = ({ event }) => {
  const [form, setForm] = useState({ name: "", email: "", distance: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/rsvp", { event_id: event.id, ...form });
      toast.success(res.data.message);
      setDone(true);
    } catch {
      toast.error("Could not RSVP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <p className="font-display text-xl mt-4" data-testid={`event-rsvp-done-${event.id}`}>SEE YOU OUT THERE.</p>;

  const input = "w-full bg-transparent border-b border-white/30 py-2.5 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200";
  return (
    <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end" data-testid={`event-rsvp-form-${event.id}`}>
      <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} aria-hidden="true" />
      <input required placeholder="NAME" data-testid={`event-rsvp-name-${event.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
      <input required type="email" data-testid={`event-rsvp-email-${event.id}`} placeholder="EMAIL" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
      <button type="submit" disabled={loading} data-testid={`event-rsvp-submit-${event.id}`} className="btn-invert bg-white text-ink font-mono-label px-6 py-3 border border-white hover:bg-transparent hover:text-white disabled:opacity-50">
        {loading ? "..." : "RSVP"}
      </button>
    </form>
  );
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setPageMeta(
      "Dallas Run Club Events: Weekly Group Runs | Ninja Run Club",
      "Ninja Run Club's rolling Dallas calendar: Saturday community runs, Track Tuesdays, HYROX run workouts, sunrise long runs, and race-week shakeouts. Pace leaders at every run, walkers welcome."
    );
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  // Audit NRC-029: Event structured data
  useEffect(() => {
    if (events.length === 0) return;
    const toISO = (e) => {
      const m = /(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(e.time || "");
      if (!m) return e.date;
      let h = parseInt(m[1], 10) % 12;
      if (/pm/i.test(m[3])) h += 12;
      return `${e.date}T${String(h).padStart(2, "0")}:${m[2]}:00-05:00`;
    };
    return setJsonLd("events-jsonld", events.map((e) => ({
      "@context": "https://schema.org",
      "@type": "Event",
      name: `${e.title} — Ninja Run Club`,
      description: e.description,
      startDate: toISO(e),
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      isAccessibleForFree: true,
      location: {
        "@type": "Place",
        name: e.location,
        address: { "@type": "PostalAddress", addressLocality: e.city || "Dallas", addressRegion: "TX", addressCountry: "US" },
      },
      organizer: { "@type": "Organization", name: "Ninja Run Club", url: SITE_BASE },
    })));
  }, [events]);

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden px-5 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <SectionVideo src="/video/run-15.mp4" srcMobile="/video/run-15-mobile.mp4" poster="/video/run-15-poster.jpg" opacity={0.25} testid="events-hero-video" />
        <div className="relative">
        <Reveal>
          <p className="font-mono-label text-white/40">SHOW UP</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">RUN WITH US.</h1>
          <p className="text-white/50 text-sm max-w-lg mt-4">
            Every NRC run has pace leaders and a no-runner-left-behind rule. Meeting points go out to the club list each week.
          </p>
        </Reveal>
        </div>
      </header>

      <Ticker items={["SATURDAY COMMUNITY RUN", "TRACK TUESDAY", "HYROX RUN WORKOUT", "SUNRISE LONG RUN", "RACE-WEEK SHAKEOUT", "WALKERS WELCOME"]} />

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-12 border-b border-white/10" data-testid="events-email-capture">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl lg:text-3xl">GET THE MEETING POINT.</h2>
          <p className="text-ink/60 text-sm mt-2 mb-6">
            Exact meeting points, weather calls, and route notes go out to the club list the week of each run.
            One email a week — the next run near you, nothing else.
          </p>
          <JoinForm compact source="events-page" />
        </div>
      </section>

      <div className="px-5 lg:px-10 py-10" data-testid="events-list">
        {events.length === 0 ? (
          <div className="border border-white/10 p-14 text-center my-10" data-testid="events-empty">
            <p className="font-display text-3xl">NO EVENTS POSTED. YET.</p>
            <p className="text-white/50 text-sm mt-3">The next block is being scheduled. Join the list to hear first.</p>
          </div>
        ) : (
          events.map((e) => {
            const d = new Date(e.date + "T12:00:00");
            const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(e.title + " - Ninja Run Club")}&dates=${e.date.replace(/-/g, "")}/${e.date.replace(/-/g, "")}&details=${encodeURIComponent(e.description)}`;
            const isOpen = open === e.id;
            return (
              <div key={e.id} className="border-b border-white/10 py-8" data-testid={`event-card-${e.id}`}>
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-12 md:col-span-2">
                    <p className="font-display text-3xl lg:text-4xl leading-none">
                      {d.toLocaleDateString("en-US", { day: "numeric" })}
                    </p>
                    <p className="font-mono-label text-white/40 mt-1">
                      {d.toLocaleDateString("en-US", { month: "short", weekday: "short" }).toUpperCase()}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="font-mono-label text-white/40">{e.type?.toUpperCase()} · {e.city?.toUpperCase()}</p>
                    <h2 className="font-display text-2xl lg:text-3xl mt-2">{e.title}</h2>
                    <p className="text-white/50 text-sm mt-2 flex items-start gap-2"><MapPin size={13} className="mt-0.5 shrink-0" /> {e.location}</p>
                    <p className="text-white/50 text-sm mt-1">{e.time} {e.timezone} · {(e.distances || []).join(" · ")} · {e.pace}</p>
                  </div>
                  <div className="col-span-12 md:col-span-4 flex md:justify-end gap-3 flex-wrap">
                    <button
                      data-testid={`event-rsvp-toggle-${e.id}`}
                      onClick={() => setOpen(isOpen ? null : e.id)}
                      className={`btn-invert font-mono-label px-6 py-3 border transition-colors duration-200 ${isOpen ? "bg-white text-ink border-white" : "border-white/40 hover:bg-white hover:text-ink"}`}
                    >
                      RSVP <ChevronDown size={12} className={`inline transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <a href={gcal} target="_blank" rel="noopener noreferrer" data-testid={`event-calendar-${e.id}`} className="flex items-center gap-2 font-mono-label text-white/50 hover:text-white px-2 py-3 transition-colors duration-200">
                      <CalendarPlus size={14} /> CALENDAR
                    </a>
                  </div>
                </div>
                {isOpen && (
                  <div className="md:pl-[16.666%]">
                    <p className="text-white/50 text-sm mt-5 max-w-xl">{e.description}</p>
                    <EventRsvp event={e} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <section className="px-5 lg:px-10 py-14 border-t border-white/10" data-testid="events-about-section">
        <h2 className="font-display text-3xl lg:text-4xl">HOW NRC RUNS WORK.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-8">
          <div className="bg-night p-6">
            <h3 className="font-mono-label text-white/40">THE RHYTHM</h3>
            <p className="text-white/60 text-sm mt-3">
              The calendar rolls weekly: Saturday community runs anchor the week, Track Tuesday handles speed, Thursday is the
              HYROX-style run workout, and long runs go off at sunrise. Race weeks add a two-mile shakeout. Same cadence, every week — that is the point.
            </p>
          </div>
          <div className="bg-night p-6">
            <h3 className="font-mono-label text-white/40">EVERY PACE, EVERY RUN</h3>
            <p className="text-white/60 text-sm mt-3">
              Pace leaders run at the front, middle, and back of every group, and a sweeper makes sure nobody finishes alone.
              Walkers are welcome at the Saturday run. If it is your first time, say so — you will get placed with the right group.
            </p>
          </div>
          <div className="bg-night p-6">
            <h3 className="font-mono-label text-white/40">NOT IN DALLAS?</h3>
            <p className="text-white/60 text-sm mt-3">
              NRC-hosted runs are Dallas-based today. Find a club near you in the{" "}
              <Link to="/clubs" className="link-underline text-white/85">national directory</Link>, check the{" "}
              <Link to="/dallas" className="link-underline text-white/85">Dallas hub</Link> if you are visiting, or train with the{" "}
              <Link to="/training" className="link-underline text-white/85">free guides</Link> until we get to your city.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
